import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useSearch } from '@tanstack/react-router';
import {
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  MessageSquarePlus,
  Pause,
  Play,
  Scale,
  ShieldAlert,
  Sparkles,
  Trash2,
  Wrench,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { OutcomePill } from '@/components/OutcomePill';
import { PageError, PageLoading, Spinner } from '@/components/Spinner';
import {
  api,
  type HeldEdit,
  type HumanOutcome,
  type RemediationState,
  type ReviewComment,
  type Run,
  type RunSummary,
  type SimilarFinding,
  type Turn,
  type WatchForVerdict,
} from '@/lib/api';
import { useAuthMe } from '@/lib/auth';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/runs')({
  component: RunsPage,
  validateSearch: (search): { rid?: string } => ({
    rid: typeof search.rid === 'string' ? search.rid : undefined,
  }),
});

// Cell slugs are BANK_PRODUCT_BUCKET — same parse the backend uses.
function splitSlug(slug: string): [string, string, string] {
  const parts = slug.split('_');
  return [parts[0] ?? '', parts[1] ?? '', parts[2] ?? ''];
}

function RunsPage() {
  const search = useSearch({ from: '/runs' });
  const [expanded, setExpanded] = useState<string | null>(search.rid ?? null);
  const { isAdmin } = useAuthMe();

  // Filters — operator-requested 2026-06-10: the flat list cluttered as
  // runs accumulated. Mirror the Prompts page's picker dimensions
  // (bank / product / bucket / language) + an ADMIN-ONLY started-by
  // filter (non-admins only ever see their own runs, so a user filter
  // would be noise for them).
  const [fBank, setFBank] = useState('all');
  const [fProduct, setFProduct] = useState('all');
  const [fBucket, setFBucket] = useState('all');
  const [fLang, setFLang] = useState('all');
  const [fUser, setFUser] = useState('all');

  useEffect(() => {
    if (search.rid) setExpanded(search.rid);
  }, [search.rid]);

  const listQ = useQuery({
    queryKey: ['runs'],
    queryFn: api.listRuns,
    refetchInterval: 4000,
  });

  const runs = useMemo(() => listQ.data?.runs ?? [], [listQ.data]);

  // Options derive from the runs the viewer can actually see — the
  // dropdowns never offer a choice that matches nothing, and newly
  // onboarded banks appear automatically.
  const options = useMemo(() => {
    const banks = new Set<string>();
    const products = new Set<string>();
    const buckets = new Set<string>();
    const users = new Set<string>();
    for (const r of runs) {
      const [b, p, k] = splitSlug(r.cell_slug);
      if (b) banks.add(b);
      if (p) products.add(p);
      if (k) buckets.add(k);
      users.add(r.started_by);
    }
    const sorted = (s: Set<string>) => [...s].sort();
    return {
      banks: sorted(banks),
      products: sorted(products),
      buckets: sorted(buckets),
      users: sorted(users),
    };
  }, [runs]);

  const filtered = useMemo(
    () =>
      runs.filter((r) => {
        const [b, p, k] = splitSlug(r.cell_slug);
        if (fBank !== 'all' && b !== fBank) return false;
        if (fProduct !== 'all' && p !== fProduct) return false;
        if (fBucket !== 'all' && k !== fBucket) return false;
        if (fLang !== 'all' && r.language !== fLang) return false;
        if (fUser !== 'all' && r.started_by !== fUser) return false;
        return true;
      }),
    [runs, fBank, fProduct, fBucket, fLang, fUser],
  );

  if (listQ.isLoading) return <PageLoading />;
  if (listQ.error) return <PageError message={String(listQ.error)} />;

  const anyFilter =
    fBank !== 'all' || fProduct !== 'all' || fBucket !== 'all' ||
    fLang !== 'all' || fUser !== 'all';

  function clearFilters() {
    setFBank('all');
    setFProduct('all');
    setFBucket('all');
    setFLang('all');
    setFUser('all');
  }

  return (
    <div className="space-y-6">
      <div>
        <h1>Runs</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Each run is one (cell, lang, prompt, persona-set, today) shipped through ConvSim + Judge.
        </p>
      </div>

      {runs.length > 0 && (
        <div className="flex flex-wrap items-end gap-3">
          <FilterSelect label="Bank" value={fBank} onChange={setFBank} options={options.banks} />
          <FilterSelect label="Product" value={fProduct} onChange={setFProduct} options={options.products} />
          <FilterSelect label="Bucket" value={fBucket} onChange={setFBucket} options={options.buckets} />
          <FilterSelect label="Language" value={fLang} onChange={setFLang} options={['EN', 'AR']} />
          {isAdmin && (
            <FilterSelect label="Started by" value={fUser} onChange={setFUser} options={options.users} />
          )}
          <div className="flex h-9 items-center gap-3 text-xs text-muted-foreground">
            <span className="tabular-nums">
              {filtered.length} of {runs.length} run{runs.length === 1 ? '' : 's'}
            </span>
            {anyFilter && (
              <button onClick={clearFilters} className="underline hover:text-foreground">
                clear filters
              </button>
            )}
          </div>
        </div>
      )}

      {runs.length === 0 ? (
        <div className="rounded-lg border border-border bg-card p-6 text-sm text-muted-foreground shadow-sm">
          No runs yet. Pick a prompt in <a className="underline" href="/prompts">Prompts</a>, compute persona state, then click <strong>Start run</strong>.
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-lg border border-border bg-card p-6 text-sm text-muted-foreground shadow-sm">
          No runs match the current filters.{' '}
          <button onClick={clearFilters} className="underline hover:text-foreground">
            Clear filters
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((s) => (
            <RunRow
              key={s.run_id}
              summary={s}
              isOpen={expanded === s.run_id}
              onToggle={() => setExpanded((prev) => (prev === s.run_id ? null : s.run_id))}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: string[];
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 rounded-md border border-input bg-card px-3 text-sm shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <option value="all">All</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </label>
  );
}

function StatusPill({ status }: { status: RunSummary['status'] }) {
  const tones: Record<RunSummary['status'], string> = {
    queued: 'border-border bg-muted text-muted-foreground',
    running: 'border-status-warning-border bg-status-warning text-status-warning-text',
    completed: 'border-success/30 bg-success/10 text-success',
    failed: 'border-destructive/30 bg-destructive/10 text-destructive',
    aborted: 'border-destructive/30 bg-destructive/10 text-destructive',
  };
  return (
    <span className={cn('inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium', tones[status])}>
      {status}
    </span>
  );
}

function DeletedBadge({
  deletedBy,
}: {
  deletedBy: string | null;
}) {
  return (
    <span
      title={
        deletedBy
          ? `Soft-deleted by ${deletedBy}. Visible to admins for audit.`
          : 'Soft-deleted. Visible to admins for audit.'
      }
      className="inline-flex items-center rounded-sm border border-status-warning-border/50 bg-status-warning px-1.5 py-px text-[10px] font-medium uppercase tracking-wide text-status-warning-text"
    >
      deleted{deletedBy ? ` · ${deletedBy}` : ''}
    </span>
  );
}

function RunRow({
  summary,
  isOpen,
  onToggle,
}: {
  summary: RunSummary;
  isOpen: boolean;
  onToggle: () => void;
}) {
  const qc = useQueryClient();
  const isDeleted = summary.deleted_at !== null;
  const deleteMut = useMutation({
    mutationFn: () => api.deleteRun(summary.run_id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['runs'] });
      qc.removeQueries({ queryKey: ['run', summary.run_id] });
      toast.success(isDeleted ? 'Run purged from disk' : 'Run deleted');
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const pauseMut = useMutation({
    mutationFn: () => api.pauseRun(summary.run_id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['runs'] });
      qc.invalidateQueries({ queryKey: ['run', summary.run_id] });
      toast.success('Pause requested — in-flight personas will finish');
    },
    onError: (e) => toast.error(String(e)),
  });
  const resumeMut = useMutation({
    mutationFn: () => api.resumeRun(summary.run_id),
    onSuccess: (run) => {
      qc.invalidateQueries({ queryKey: ['runs'] });
      toast.success(`Resumed as ${run.run_id}`);
    },
    onError: (e) => toast.error(String(e)),
  });

  function onDelete(e: React.MouseEvent) {
    e.stopPropagation();
    const ok = window.confirm(
      isDeleted
        ? `PERMANENTLY PURGE run ${summary.run_id}?\n\nThis run is already soft-deleted. Confirming will rmtree the entire run directory (run.json, every persona's sim + verdict, comments). Cannot be undone.`
        : `Delete run ${summary.run_id}?\n\nThis soft-deletes the run — admins will still see it marked "deleted by ${summary.started_by}". You can purge it from disk later.`,
    );
    if (!ok) return;
    deleteMut.mutate();
  }
  function onPause(e: React.MouseEvent) {
    e.stopPropagation();
    pauseMut.mutate();
  }
  function onResume(e: React.MouseEvent) {
    e.stopPropagation();
    resumeMut.mutate();
  }

  return (
    <div
      className={cn(
        'overflow-hidden rounded-lg border border-border bg-card shadow-sm',
        isDeleted && 'border-status-warning-border/50 bg-status-warning/30 opacity-80',
      )}
    >
      <div className="flex w-full items-center justify-between gap-4 px-5 py-3 hover:bg-accent/30">
        <button
          onClick={onToggle}
          className="flex flex-1 min-w-0 items-center gap-3 text-left"
        >
          {isOpen ? (
            <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-sm">
              <span className={cn('font-medium', isDeleted && 'line-through text-muted-foreground')}>
                {summary.prompt_name}
              </span>
              <span className="text-muted-foreground">v{summary.prompt_version}</span>
              <span className="text-muted-foreground">·</span>
              <span className="font-mono text-xs text-muted-foreground">{summary.cell_slug}</span>
              <span className="text-muted-foreground">·</span>
              <span className="text-xs text-muted-foreground">{summary.language}</span>
              {summary.imported && (
                <span
                  title="Imported real-call transcript graded against the cell rulebooks (not a simulation)."
                  className="inline-flex items-center rounded-sm border border-status-purple-border/60 bg-status-purple px-1.5 py-px text-[10px] font-medium uppercase tracking-wide text-status-purple-text"
                >
                  imported
                </span>
              )}
              {isDeleted && <DeletedBadge deletedBy={summary.deleted_by} />}
            </div>
            <div className="mt-0.5 truncate font-mono text-xs text-muted-foreground">
              {summary.run_id} · today {summary.today ?? '?'} · started by {summary.started_by}
            </div>
          </div>
        </button>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <StatusPill status={summary.status} />
          {summary.status === 'running' ? (
            <span className="inline-flex items-center gap-1.5 tabular-nums">
              <Spinner className="size-3" />
              {summary.persona_completed} / {summary.persona_total} done
            </span>
          ) : (
            <span className="tabular-nums">
              {summary.pass_count}/{summary.persona_completed} pass
            </span>
          )}
          <span className="tabular-nums">${summary.total_cost_usd.toFixed(4)}</span>
          {summary.status === 'running' && !isDeleted && (
            <button
              onClick={onPause}
              disabled={pauseMut.isPending}
              title="Pause: stop queuing new personas; in-flight finish naturally"
              className="inline-flex size-7 items-center justify-center rounded-md border border-border bg-card text-muted-foreground hover:border-status-warning-border/40 hover:bg-status-warning hover:text-status-warning-text disabled:opacity-50"
            >
              {pauseMut.isPending ? <Spinner className="size-3" /> : <Pause className="size-3.5" />}
            </button>
          )}
          {summary.status === 'aborted' && !isDeleted && (
            <button
              onClick={onResume}
              disabled={resumeMut.isPending}
              title="Resume: spawn a new run with the personas that didn't complete"
              className="inline-flex size-7 items-center justify-center rounded-md border border-border bg-card text-muted-foreground hover:border-success/40 hover:bg-success/10 hover:text-success disabled:opacity-50"
            >
              {resumeMut.isPending ? <Spinner className="size-3" /> : <Play className="size-3.5" />}
            </button>
          )}
          <button
            onClick={onDelete}
            disabled={deleteMut.isPending}
            title={
              isDeleted
                ? 'Purge: permanently rmtree the run dir from disk (admin only — already soft-deleted)'
                : "Soft-delete this run (admins keep visibility with a 'deleted by' marker)"
            }
            className={cn(
              'inline-flex size-7 items-center justify-center rounded-md border bg-card disabled:opacity-50',
              isDeleted
                ? 'border-destructive/40 bg-destructive/10 text-destructive hover:border-destructive hover:bg-destructive hover:text-destructive-foreground'
                : 'border-border text-muted-foreground hover:border-destructive/40 hover:bg-destructive/10 hover:text-destructive',
            )}
          >
            {deleteMut.isPending ? <Spinner className="size-3" /> : <Trash2 className="size-3.5" />}
          </button>
        </div>
      </div>
      {isOpen && <RunDetail rid={summary.run_id} />}
    </div>
  );
}

function RunDetail({ rid }: { rid: string }) {
  const runQ = useQuery({
    queryKey: ['run', rid],
    queryFn: () => api.getRun(rid),
    refetchInterval: (q) =>
      q.state.data && (q.state.data.status === 'completed' || q.state.data.status === 'failed')
        ? false
        : 3000,
  });

  if (runQ.isLoading) return <PageLoading />;
  if (runQ.error || !runQ.data) return <PageError message={String(runQ.error)} />;
  const run = runQ.data;
  return (
    <div className="border-t border-border bg-muted/20 p-5">
      <div className="space-y-6">
        {/* Remediation re-simulates the failing personas — meaningless
            for an imported real call (no personas to re-run). */}
        {!run.imported && <RemediationPanel run={run} />}
        {run.persona_results.map((pr) => (
          <PersonaResultPanel key={pr.persona_id} rid={run.run_id} result={pr} />
        ))}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Remediation (v10, admin-only)
// ────────────────────────────────────────────────────────────────────

function RemediationPanel({ run }: { run: Run }) {
  const { isAdmin } = useAuthMe();
  const qc = useQueryClient();
  const rid = run.run_id;

  const stateQ = useQuery({
    queryKey: ['remediation', rid],
    queryFn: () => api.getRemediation(rid),
    enabled: isAdmin,
    refetchInterval: (q) => (q.state.data?.status === 'running' ? 2000 : false),
  });

  const startMut = useMutation({
    mutationFn: () => api.startRemediation(rid, {}),
    onSuccess: () => {
      toast.success('Remediation started — closing the failing findings');
      qc.invalidateQueries({ queryKey: ['remediation', rid] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const abortMut = useMutation({
    mutationFn: () => api.abortRemediation(rid),
    onSuccess: () => {
      toast.success('Abort requested — in-flight work will stop at the next checkpoint');
      qc.invalidateQueries({ queryKey: ['remediation', rid] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const approveMut = useMutation({
    mutationFn: (editId: string) => api.approveRemediationEdits(rid, [editId]),
    onSuccess: () => {
      toast.success('Edit approved — it will apply on the next remediation run');
      qc.invalidateQueries({ queryKey: ['remediation', rid] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (!isAdmin) return null;

  const failCount = run.persona_results.reduce(
    (acc, pr) => acc + (pr.verdict?.fail_count ?? 0),
    0,
  );
  const terminal = run.status === 'completed' || run.status === 'aborted';
  const state = stateQ.data ?? null;

  if (!state) {
    if (!terminal || failCount === 0) return null;
    return (
      <div className="rounded-lg border border-border bg-card p-4 shadow-sm">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-sm font-medium">
              <Wrench className="size-4 text-primary" />
              Remediation
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              {failCount} failing finding{failCount === 1 ? '' : 's'} in this run.
              Remediation proposes surgical edits to the prompt, re-tests only the
              failing personas, and banks each improving version. Works with or
              without review comments.
            </p>
          </div>
          <button
            onClick={() => {
              if (window.confirm(
                `Remediate ${run.prompt_name} v${run.prompt_version}?\n\nThis spends LLM tokens: edit proposals + a re-test of the failing personas per iteration (max 3). The original prompt is never modified — improving candidates are banked as new versions.`,
              )) startMut.mutate();
            }}
            disabled={startMut.isPending}
            className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground shadow-sm hover:bg-primary/90 disabled:opacity-60"
          >
            {startMut.isPending ? <Spinner className="size-4 text-primary-foreground" /> : <Wrench className="size-4" />}
            Remediate
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-primary/30 bg-card p-4 shadow-sm">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Wrench className="size-4 text-primary" />
          Remediation
          <RemediationStatusPill status={state.status} />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs tabular-nums text-muted-foreground">
            ${state.total_cost_usd.toFixed(4)}
          </span>
          {state.status === 'running' ? (
            <button
              onClick={() => abortMut.mutate()}
              disabled={abortMut.isPending}
              className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border bg-card px-2.5 text-xs font-medium text-muted-foreground hover:border-destructive/40 hover:text-destructive disabled:opacity-50"
            >
              {abortMut.isPending ? <Spinner className="size-3" /> : <Pause className="size-3.5" />}
              Abort
            </button>
          ) : (
            <button
              onClick={() => {
                if (window.confirm('Run remediation again? Approved audit-sensitive edits will be applied this time.')) startMut.mutate();
              }}
              disabled={startMut.isPending}
              className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border bg-card px-2.5 text-xs font-medium text-primary hover:bg-accent disabled:opacity-50"
            >
              {startMut.isPending ? <Spinner className="size-3" /> : <Play className="size-3.5" />}
              Run again
            </button>
          )}
        </div>
      </div>

      <div className="mt-2 text-xs text-muted-foreground">
        {state.status === 'running' && <Spinner className="mr-1.5 inline size-3" />}
        {state.phase}
        {state.error && (
          <span className="ml-2 text-destructive">{state.error}</span>
        )}
      </div>

      {state.history.length > 0 && (
        <table className="mt-3 w-full text-xs">
          <thead className="text-left uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="py-1 pr-3 font-medium">Iter</th>
              <th className="py-1 pr-3 font-medium">Edits</th>
              <th className="py-1 pr-3 font-medium">Resolved</th>
              <th className="py-1 pr-3 font-medium">New issues</th>
              <th className="py-1 pr-3 font-medium">Banked</th>
            </tr>
          </thead>
          <tbody>
            {state.history.map((h) => (
              <tr key={h.iteration} className="border-t border-border">
                <td className="py-1.5 pr-3">{h.iteration}</td>
                <td className="py-1.5 pr-3 tabular-nums">
                  {h.edits_applied} applied · {h.edits_skipped} skipped
                  {h.edits_held > 0 ? ` · ${h.edits_held} held` : ''}
                </td>
                <td className="py-1.5 pr-3 tabular-nums">
                  {h.resolved}/{h.n0} ({Math.round(h.pct * 100)}%)
                </td>
                <td className={cn('py-1.5 pr-3 tabular-nums', h.new_issues > 0 && 'text-destructive')}>
                  {h.new_issues}
                </td>
                <td className="py-1.5 pr-3">
                  {h.banked_version != null ? (
                    <span className="font-medium text-success">
                      v{h.banked_version}
                    </span>
                  ) : (
                    '—'
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {state.best_version != null && state.status !== 'running' && (
        <p className="mt-2 text-xs text-muted-foreground">
          Best candidate banked as{' '}
          <span className="font-medium text-foreground">
            {state.prompt_name} v{state.best_version}
          </span>{' '}
          — review it on the Prompts page (Diff against v{state.prompt_version}), then
          promote it when you're happy.
        </p>
      )}

      {state.held_audit_sensitive.length > 0 && (
        <div className="mt-3 space-y-2 rounded-md border border-status-warning-border/40 bg-status-warning/50 p-3">
          <div className="flex items-center gap-1.5 text-xs font-medium text-status-warning-text">
            <ShieldAlert className="size-3.5" />
            Audit-sensitive edits held for approval — never auto-applied
          </div>
          {state.held_audit_sensitive.map((h) => (
            <HeldEditRow
              key={h.edit_id}
              edit={h}
              onApprove={() => approveMut.mutate(h.edit_id)}
              approving={approveMut.isPending}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function RemediationStatusPill({ status }: { status: RemediationState['status'] }) {
  const styles: Record<string, string> = {
    running: 'bg-primary/10 text-primary',
    completed: 'bg-success/10 text-success',
    failed: 'bg-destructive/10 text-destructive',
    aborted: 'bg-status-warning text-status-warning-text',
  };
  return (
    <span className={cn('rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide', styles[status])}>
      {status}
    </span>
  );
}

function HeldEditRow({
  edit,
  onApprove,
  approving,
}: {
  edit: HeldEdit;
  onApprove: () => void;
  approving: boolean;
}) {
  return (
    <div className="rounded-md border border-status-warning-border/50 bg-card p-2.5 text-xs">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <div className="text-[10px] uppercase tracking-wide text-status-warning-text">
            {edit.held_reason}
          </div>
          <div className="font-mono">
            <span className="text-destructive line-through">{edit.find.slice(0, 160)}</span>
          </div>
          <div className="font-mono">
            <span className="text-success">{edit.replace.slice(0, 160)}</span>
          </div>
          {edit.why && <div className="text-muted-foreground">{edit.why}</div>}
        </div>
        {edit.approved ? (
          <span className="inline-flex shrink-0 items-center gap-1 rounded-sm bg-success/10 px-1.5 py-0.5 text-[10px] font-medium uppercase text-success">
            <CheckCircle2 className="size-3" /> approved
          </span>
        ) : (
          <button
            onClick={() => {
              if (window.confirm(
                'Approve this audit-sensitive edit?\n\nIt touches regulated wording. It will be applied on the NEXT remediation run.',
              )) onApprove();
            }}
            disabled={approving}
            className="inline-flex h-7 shrink-0 items-center rounded-md border border-status-warning-border/60 bg-status-warning px-2 text-[11px] font-medium text-status-warning-text hover:bg-status-warning disabled:opacity-50"
          >
            Approve
          </button>
        )}
      </div>
    </div>
  );
}

function PersonaResultPanel({
  rid,
  result,
}: {
  rid: string;
  result: Run['persona_results'][number];
}) {
  // Collect every FAIL verdict's evidence quote so the transcript can
  // visually highlight the offending agent line in red.
  const failQuotes = useMemo(() => {
    if (!result.verdict) return [] as string[];
    return result.verdict.watch_for_verdicts
      .filter((v) => v.outcome === 'fail' && v.evidence_quote.trim().length > 0)
      .map((v) => v.evidence_quote.trim());
  }, [result.verdict]);

  // Pull existing human-verdict comments for this persona so the
  // transcript view can show them inline below the turn they reference.
  const commentsQ = useQuery({
    queryKey: ['comments', rid],
    queryFn: () => api.listComments(rid),
  });
  const humanVerdicts = useMemo(
    () =>
      (commentsQ.data?.comments ?? []).filter(
        (c) => c.persona_id === result.persona_id && c.comment_kind === 'human_verdict',
      ),
    [commentsQ.data, result.persona_id],
  );

  return (
    <div className="rounded-lg border border-border bg-card p-4 shadow-sm">
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0">
          <div className="text-sm font-medium">{result.persona_name}</div>
          <div className="font-mono text-xs text-muted-foreground">{result.persona_id}</div>
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          {result.verdict && <><OutcomePill outcome={result.verdict.overall} />{result.verdict.evaluation_status && result.verdict.evaluation_status !== 'complete' && <span className="rounded border border-status-warning-border bg-status-warning px-2 py-1 text-[11px] text-status-warning-text">{result.verdict.evaluation_status.replaceAll('_',' ')}</span>}</>}
          {humanVerdicts.length > 0 && (
            <span
              className="inline-flex items-center rounded-full border border-status-warning-border/50 bg-status-warning px-2 py-0.5 text-[11px] font-medium text-status-warning-text"
              title="Human reviewer added their own judgement"
            >
              {humanVerdicts.length} human {humanVerdicts.length === 1 ? 'verdict' : 'verdicts'}
            </span>
          )}
          <span className="tabular-nums">${result.total_cost_usd.toFixed(4)}</span>
        </div>
      </div>

      {result.error && (
        <div className="mt-3 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
          {result.error}
        </div>
      )}

      {result.sim && (
        <TranscriptSection
          rid={rid}
          personaId={result.persona_id}
          turns={result.sim.turns}
          disconnectFired={result.sim.disconnect_fired}
          turnCapHit={result.sim.turn_cap_hit}
          failQuotes={failQuotes}
          humanVerdicts={humanVerdicts}
        />
      )}

      {result.sim && <details className="mt-3 rounded border border-border p-3"><summary className="cursor-pointer text-xs font-medium">{result.sim.rendered_prompt_complete ? 'Complete frozen prompt' : 'Historical prompt preview · full artifact unavailable'}</summary><pre dir="auto" className="mt-3 max-h-96 overflow-auto whitespace-pre-wrap text-sm leading-relaxed">{result.sim.rendered_prompt_complete ? result.sim.rendered_prompt : result.sim.rendered_prompt_preview}</pre></details>}

      {result.sim?.persona_state && (
        <VariablesPanel state={result.sim.persona_state} />
      )}

      {result.verdict && (
        <VerdictGrid rid={rid} personaId={result.persona_id} verdict={result.verdict} />
      )}
    </div>
  );
}

function TranscriptSection({
  rid,
  personaId,
  turns,
  disconnectFired,
  turnCapHit,
  failQuotes,
  humanVerdicts,
}: {
  rid: string;
  personaId: string;
  turns: Turn[];
  disconnectFired: boolean;
  turnCapHit: boolean;
  failQuotes: string[];
  humanVerdicts: ReviewComment[];
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  // Floating "Add judgement" pill: anchored to the selection rectangle.
  // null = no active selection on this transcript.
  const [pending, setPending] = useState<{
    quote: string;
    turnIndex: number;
    x: number;
    y: number;
  } | null>(null);

  function onMouseUp(e: React.MouseEvent) {
    // Clicks INSIDE the judgement popover (PASS/FAIL buttons, the
    // comment textarea, Save/Cancel) collapse the text selection and
    // bubble up here — without this guard the popover closed the
    // moment the reviewer clicked an outcome or the comment box
    // (operator-reported 2026-06-10). The popover manages its own
    // lifecycle; only clicks on the transcript itself may close it.
    const target = e.target as HTMLElement | null;
    if (target?.closest?.('[data-judgement-popover]')) {
      return;
    }
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed) {
      setPending(null);
      return;
    }
    const text = sel.toString().trim();
    if (text.length < 3) {
      setPending(null);
      return;
    }
    // Only react to selections entirely within THIS transcript section.
    const range = sel.getRangeAt(0);
    if (!containerRef.current || !containerRef.current.contains(range.commonAncestorContainer)) {
      return;
    }
    // Walk up from the focus node to find the turn index data attribute.
    let node: Node | null = sel.focusNode;
    let turnIndex = -1;
    while (node && node !== containerRef.current) {
      if (node instanceof HTMLElement && node.dataset.turnIndex) {
        turnIndex = parseInt(node.dataset.turnIndex, 10);
        break;
      }
      node = node.parentNode;
    }
    if (turnIndex < 0) return;
    const rect = range.getBoundingClientRect();
    const containerRect = containerRef.current.getBoundingClientRect();
    setPending({
      quote: text,
      turnIndex,
      x: rect.left - containerRect.left + rect.width / 2,
      y: rect.bottom - containerRect.top + 4,
    });
  }

  return (
    <details className="mt-4" open={false}>
      <summary className="cursor-pointer select-none text-xs font-medium uppercase tracking-wide text-muted-foreground">
        transcript ({turns.length} turns · {disconnectFired ? '(disconnect)' : turnCapHit ? 'cap hit' : 'ended'})
        {failQuotes.length > 0 && (
          <span className="ml-2 text-destructive/80 normal-case">
            · {failQuotes.length} fail highlight{failQuotes.length === 1 ? '' : 's'}
          </span>
        )}
        {humanVerdicts.length > 0 && (
          <span className="ml-2 text-status-warning-text normal-case">
            · {humanVerdicts.length} human note{humanVerdicts.length === 1 ? '' : 's'}
          </span>
        )}
      </summary>
      <div className="mt-2 text-[11px] text-muted-foreground">
        Tip: highlight any text in the agent's or customer's line and click <strong>Add judgement</strong> to capture a comment with an outcome (pass / fail / n/a). Saved alongside the run.
      </div>
      <div
        ref={containerRef}
        onMouseUp={onMouseUp}
        className="relative mt-3 space-y-2 rounded-md border border-border bg-background p-3 text-sm"
      >
        {turns.map((t) => (
          <div key={t.index} data-turn-index={t.index}>
            <TranscriptTurn turn={t} failQuotes={failQuotes} />
            {humanVerdicts
              .filter((c) => c.turn_index === t.index)
              .map((c) => (
                <HumanVerdictNote key={c.comment_id} comment={c} />
              ))}
          </div>
        ))}
        {pending && (
          <AddJudgementPopover
            rid={rid}
            personaId={personaId}
            turnIndex={pending.turnIndex}
            quote={pending.quote}
            x={pending.x}
            y={pending.y}
            onClose={() => {
              setPending(null);
              window.getSelection()?.removeAllRanges();
            }}
          />
        )}
      </div>
    </details>
  );
}

function HumanVerdictNote({ comment }: { comment: ReviewComment }) {
  const tone =
    comment.human_outcome === 'fail'
      ? 'border-destructive/40 bg-destructive/5'
      : comment.human_outcome === 'pass'
      ? 'border-success/40 bg-success/5'
      : 'border-status-warning-border/40 bg-status-warning';
  return (
    <div className={cn('mt-1 ml-20 rounded-md border px-2.5 py-1.5 text-xs', tone)}>
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center rounded-sm border border-status-warning-border/50 bg-status-warning px-1.5 py-px text-[10px] font-medium uppercase tracking-wide text-status-warning-text">
          human
        </span>
        {comment.human_outcome && <OutcomePill outcome={comment.human_outcome} />}
        <span className="text-muted-foreground">{comment.author}</span>
      </div>
      {comment.evidence_quote && (
        <div className="mt-1 border-l-2 border-border pl-2 italic text-muted-foreground">
          "{comment.evidence_quote}"
        </div>
      )}
      <div className="mt-1 text-foreground">{comment.body}</div>
    </div>
  );
}

function AddJudgementPopover({
  rid,
  personaId,
  turnIndex,
  quote,
  x,
  y,
  onClose,
}: {
  rid: string;
  personaId: string;
  turnIndex: number;
  quote: string;
  x: number;
  y: number;
  onClose: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [outcome, setOutcome] = useState<HumanOutcome>('fail');
  const [body, setBody] = useState('');
  const qc = useQueryClient();
  const saveMut = useMutation({
    mutationFn: () =>
      api.addComment(rid, {
        persona_id: personaId,
        watch_for_index: -1,
        body: body.trim(),
        evidence_quote: quote,
        comment_kind: 'human_verdict',
        turn_index: turnIndex,
        human_outcome: outcome,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['comments', rid] });
      toast.success('Human judgement saved');
      onClose();
    },
    onError: (e) => toast.error(String(e)),
  });

  if (!open) {
    return (
      <button
        data-judgement-popover
        onClick={() => setOpen(true)}
        onMouseDown={(e) => e.preventDefault()}
        style={{ left: x, top: y, transform: 'translateX(-50%)' }}
        className="absolute z-10 inline-flex items-center gap-1.5 rounded-md border border-status-warning-border/60 bg-status-warning px-2.5 py-1 text-xs font-medium text-status-warning-text shadow-sm hover:bg-status-warning"
      >
        <MessageSquarePlus className="size-3.5" />
        Add judgement on selection
      </button>
    );
  }

  return (
    <div
      data-judgement-popover
      style={{ left: Math.max(0, x - 160), top: y, width: 360 }}
      className="absolute z-10 rounded-md border border-border bg-card p-3 text-sm shadow-lg"
    >
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Add human judgement
      </div>
      <div className="mt-1 border-l-2 border-status-warning-border/60 bg-status-warning/40 pl-2 text-xs italic text-muted-foreground">
        "{quote}"
      </div>
      <div className="mt-2 flex gap-1.5">
        {(['pass', 'fail', 'n/a'] as HumanOutcome[]).map((o) => (
          <button
            key={o}
            onClick={() => setOutcome(o)}
            className={cn(
              'rounded-md border px-2 py-1 text-xs font-medium uppercase',
              outcome === o
                ? o === 'fail'
                  ? 'border-destructive/40 bg-destructive/10 text-destructive'
                  : o === 'pass'
                  ? 'border-success/40 bg-success/10 text-success'
                  : 'border-border bg-muted text-foreground'
                : 'border-border bg-card text-muted-foreground hover:bg-accent',
            )}
          >
            {o}
          </button>
        ))}
      </div>
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={3}
        placeholder="What did the judge miss? Why does this span warrant a different outcome?"
        className="mt-2 w-full rounded-md border border-input bg-card p-2 text-sm shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />
      <div className="mt-2 flex gap-1.5">
        <button
          onClick={() => saveMut.mutate()}
          disabled={saveMut.isPending || !body.trim()}
          className="inline-flex h-8 items-center gap-1.5 rounded-md bg-primary px-3 text-xs font-medium text-primary-foreground shadow-sm hover:bg-primary/90 disabled:opacity-50"
        >
          {saveMut.isPending && <Spinner className="size-3 text-primary-foreground" />}
          Save
        </button>
        <button
          onClick={onClose}
          className="inline-flex h-8 items-center rounded-md border border-border bg-card px-3 text-xs font-medium text-muted-foreground hover:bg-accent"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function VariablesPanel({ state }: { state: NonNullable<Run['persona_results'][number]['sim']>['persona_state'] }) {
  if (!state) return null;
  // Stable display order matching the mental model the operator built
  // around the state-derivation pipeline: dates first, money next,
  // identity last. ``extras`` (operator overrides + persona dummies)
  // get their own block so they don't drown out the named fields.
  const dates: Array<[string, string | null | undefined]> = [
    ['today', state.today],
    ['dpd', String(state.dpd)],
    ['due_date', state.due_date],
    ['next_due_date', state.next_due_date],
    ['statement_date', state.statement_date],
    ['month_end', state.month_end],
    ['latest_allowed_ptp_date', state.latest_allowed_ptp_date],
  ];
  const money: Array<[string, string | null | undefined]> = [
    ['overdue_amount', state.overdue_amount],
    ['installment_amount', state.installment_amount],
    ['two_installments_amount', state.two_installments_amount],
    ['total_outstanding', state.total_outstanding],
  ];
  const identity: Array<[string, string | null | undefined]> = [
    ['title', state.title],
    ['first_name', state.first_name],
    ['last_name', state.last_name],
    ['agent_name', state.agent_name],
    ['reason_non_payment', state.reason_non_payment],
    ['ptp_date', state.ptp_date],
    ['ptp_amount', state.ptp_amount],
    ['ptp_solution', state.ptp_solution],
  ];
  const extras = Object.entries(state.extras ?? {});
  const namedCount =
    dates.filter(([, v]) => v != null && v !== '').length +
    money.filter(([, v]) => v != null && v !== '').length +
    identity.filter(([, v]) => v != null && v !== '').length;

  return (
    <details className="mt-4">
      <summary className="cursor-pointer select-none text-xs font-medium uppercase tracking-wide text-muted-foreground">
        variables ({namedCount} named{extras.length ? ` · ${extras.length} extras` : ''})
      </summary>
      <div className="mt-3 grid grid-cols-1 gap-4 rounded-md border border-border bg-background p-3 text-sm md:grid-cols-3">
        <VariablesGroup title="Dates" entries={dates} />
        <VariablesGroup title="Money" entries={money} />
        <VariablesGroup title="Identity" entries={identity} />
      </div>
      {extras.length > 0 && (
        <div className="mt-2 rounded-md border border-border bg-background p-3">
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            extras
          </div>
          <div className="mt-2 grid grid-cols-1 gap-x-4 gap-y-1 md:grid-cols-2">
            {extras.map(([k, v]) => (
              <VariableLine key={k} name={k} value={v} />
            ))}
          </div>
        </div>
      )}
    </details>
  );
}

function VariablesGroup({
  title,
  entries,
}: {
  title: string;
  entries: Array<[string, string | null | undefined]>;
}) {
  return (
    <div>
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {title}
      </div>
      <div className="mt-1 space-y-0.5">
        {entries.map(([k, v]) => (
          <VariableLine key={k} name={k} value={v} />
        ))}
      </div>
    </div>
  );
}

function VariableLine({ name, value }: { name: string; value: string | null | undefined }) {
  const empty = value == null || value === '';
  return (
    <div className="flex gap-2 text-xs">
      <span className="font-mono text-muted-foreground">{name}</span>
      <span className={cn('flex-1 truncate', empty ? 'italic text-muted-foreground/60' : 'text-foreground')}>
        {empty ? '—' : value}
      </span>
    </div>
  );
}

function TranscriptTurn({ turn, failQuotes }: { turn: Turn; failQuotes: string[] }) {
  const isStatic = turn.model === 'static';
  // A turn is "failed" if any fail-evidence quote is contained in its
  // text. We highlight the offending substring(s) inline and tint the
  // turn row so the operator can scan failures at a glance.
  // Static prelude turns are excluded from failure highlighting — the
  // judge no longer grades them, so there's no fair fail to attribute.
  const matches = useMemo(() => {
    if (isStatic) return [] as string[];
    const hits: string[] = [];
    for (const q of failQuotes) {
      if (q.length >= 4 && turn.text.includes(q)) hits.push(q);
    }
    return hits;
  }, [turn.text, failQuotes, isStatic]);
  const failed = matches.length > 0;

  return (
    <div
      className={cn(
        'flex gap-2 rounded-sm px-1 py-0.5 -mx-1',
        failed && 'bg-destructive/5',
        isStatic && 'opacity-60',
      )}
    >
      <span
        className={cn(
          'w-20 shrink-0 text-xs font-medium uppercase tracking-wide',
          turn.role === 'agent' ? 'text-primary' : 'text-status-warning-text',
        )}
      >
        {turn.role}
      </span>
      <span className="flex-1 text-foreground">
        {failed ? <HighlightedText text={turn.text} highlights={matches} /> : turn.text}
        {isStatic && (
          <span
            className="ml-2 inline-flex items-center rounded-sm border border-border bg-muted px-1.5 py-px text-[10px] font-medium uppercase tracking-wide text-muted-foreground"
            title="System-injected phone-pickup greeting. Not graded by the judge."
          >
            static
          </span>
        )}
      </span>
    </div>
  );
}

function HighlightedText({ text, highlights }: { text: string; highlights: string[] }) {
  // Greedy left-to-right substring highlight. For each match found in
  // `text`, wrap it in a red span. Overlapping matches resolve to the
  // earliest one; the rest are dropped (rare in practice).
  type Part = { text: string; hit: boolean };
  const parts: Part[] = [];
  let cursor = 0;
  while (cursor < text.length) {
    let nextIdx = -1;
    let nextLen = 0;
    for (const h of highlights) {
      const i = text.indexOf(h, cursor);
      if (i === -1) continue;
      if (nextIdx === -1 || i < nextIdx || (i === nextIdx && h.length > nextLen)) {
        nextIdx = i;
        nextLen = h.length;
      }
    }
    if (nextIdx === -1) {
      parts.push({ text: text.slice(cursor), hit: false });
      break;
    }
    if (nextIdx > cursor) parts.push({ text: text.slice(cursor, nextIdx), hit: false });
    parts.push({ text: text.slice(nextIdx, nextIdx + nextLen), hit: true });
    cursor = nextIdx + nextLen;
  }
  return (
    <>
      {parts.map((p, i) =>
        p.hit ? (
          <span
            key={i}
            className="rounded-sm bg-destructive/15 px-0.5 font-medium text-destructive underline decoration-destructive/40 decoration-dotted underline-offset-2"
          >
            {p.text}
          </span>
        ) : (
          <span key={i}>{p.text}</span>
        ),
      )}
    </>
  );
}

function VerdictGrid({
  rid,
  personaId,
  verdict,
}: {
  rid: string;
  personaId: string;
  verdict: Run['persona_results'][number]['verdict'] & object;
}) {
  // The whole Watch_for block is one collapsible group, default
  // collapsed (operator-locked 2026-06-08). Inside, each card still
  // has its own toggle and Expand-all / Collapse-all chips override.
  const [groupOpen, setGroupOpen] = useState(false);
  const [allExpanded, setAllExpanded] = useState<boolean | null>(null);
  return (
    <div className="mt-4">
      <button
        onClick={() => setGroupOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-2 rounded-md border border-border bg-muted/30 px-3 py-2 text-left hover:bg-accent/30"
      >
        <div className="flex items-center gap-2">
          {groupOpen ? (
            <ChevronDown className="size-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="size-4 text-muted-foreground" />
          )}
          <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Watch_for ({verdict.pass_count} pass · {verdict.fail_count} fail · {verdict.na_count} n/a)
          </span>
        </div>
      </button>
      {groupOpen && (
        <div className="mt-2 space-y-2">
          <div className="flex items-center justify-end gap-1.5 text-xs text-muted-foreground">
            <button
              onClick={() => setAllExpanded(true)}
              className="rounded-md border border-border bg-card px-2 py-0.5 hover:bg-accent"
            >
              Expand all
            </button>
            <button
              onClick={() => setAllExpanded(false)}
              className="rounded-md border border-border bg-card px-2 py-0.5 hover:bg-accent"
            >
              Collapse all
            </button>
          </div>
          {verdict.watch_for_verdicts.map((v) => (
            <VerdictRow
              key={v.watch_for_index}
              verdict={v}
              rid={rid}
              personaId={personaId}
              forceExpanded={allExpanded}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function VerdictRow({
  verdict,
  rid,
  personaId,
  forceExpanded,
}: {
  verdict: WatchForVerdict;
  rid: string;
  personaId: string;
  forceExpanded: boolean | null;
}) {
  // Default: FAIL expanded (operator needs to see what failed), PASS
  // and N/A collapsed (one-line summary unless clicked). Expand-all /
  // Collapse-all from the header overrides the per-row default.
  const defaultOpen = verdict.outcome === 'fail';
  const [open, setOpen] = useState(defaultOpen);
  const [showCommentForm, setShowCommentForm] = useState(false);
  const [showOverruleForm, setShowOverruleForm] = useState(false);

  useEffect(() => {
    if (forceExpanded === null) return;
    setOpen(forceExpanded);
  }, [forceExpanded]);

  return (
    <div
      className={cn(
        'rounded-md border bg-card text-sm shadow-sm',
        verdict.outcome === 'fail'
          ? 'border-destructive/30'
          : verdict.outcome === 'pass'
          ? 'border-success/20'
          : 'border-border',
      )}
    >
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-start justify-between gap-3 px-3 py-2 text-left hover:bg-accent/30"
      >
        <div className="flex min-w-0 flex-1 items-start gap-2">
          {open ? (
            <ChevronDown className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <OutcomePill outcome={verdict.outcome} />
              <span className="text-xs text-muted-foreground">#{verdict.watch_for_index}</span>
              {verdict.severity && (
                <span
                  className={cn(
                    'rounded-sm border px-1.5 py-px text-[10px] font-semibold uppercase tracking-wide',
                    verdict.severity === 'critical'
                      ? 'border-destructive/50 bg-destructive/10 text-destructive'
                      : verdict.severity === 'major'
                      ? 'border-status-warning-border/60 bg-status-warning text-status-warning-text'
                      : 'border-border bg-muted text-muted-foreground',
                  )}
                >
                  {verdict.severity}
                </span>
              )}
              {verdict.source === 'shared' && (
                <span
                  title="Shared rule for this call type (from the cell's shared checklist), not this persona's own watch_for."
                  className="rounded-sm border border-sky-300/60 bg-sky-50 px-1.5 py-px text-[10px] font-medium uppercase tracking-wide text-sky-700"
                >
                  shared
                </span>
              )}
            </div>
            <div className={cn('mt-1 text-sm', open ? 'text-foreground' : 'truncate text-muted-foreground')}>
              {verdict.watch_for_text}
            </div>
          </div>
        </div>
      </button>
      {open && (
        <div className="px-3 pb-3 pl-9">
          {verdict.evidence_quote && (
            <div className="mt-1 border-l-2 border-border pl-3 text-xs italic text-muted-foreground">
              "{verdict.evidence_quote}"
            </div>
          )}
          {verdict.reasoning && (
            <div className="mt-1.5 text-xs text-muted-foreground">{verdict.reasoning}</div>
          )}
          <div className="mt-2 flex gap-2">
            <button
              onClick={() => setShowCommentForm((v) => !v)}
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-2.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            >
              <MessageSquarePlus className="size-3.5" />
              Comment
            </button>
            {verdict.outcome === 'fail' && (
              <button
                onClick={() => setShowOverruleForm((v) => !v)}
                title="The judge got this wrong — file an overrule for admin approval. Once approved, the judge stops failing this behavior in every future run of this cell."
                className="inline-flex items-center gap-1.5 rounded-md border border-status-warning-border/60 bg-status-warning/60 px-2.5 py-1 text-xs font-medium text-status-warning-text transition-colors hover:bg-status-warning"
              >
                <Scale className="size-3.5" />
                Judge wrong?
              </button>
            )}
          </div>
          {showCommentForm && (
            <CommentForm
              rid={rid}
              personaId={personaId}
              watchForIndex={verdict.watch_for_index}
              evidenceQuote={verdict.evidence_quote}
              onDone={() => setShowCommentForm(false)}
            />
          )}
          {showOverruleForm && (
            <OverruleForm
              rid={rid}
              personaId={personaId}
              watchForIndex={verdict.watch_for_index}
              onDone={() => setShowOverruleForm(false)}
            />
          )}
        </div>
      )}
    </div>
  );
}

function OverruleForm({
  rid,
  personaId,
  watchForIndex,
  onDone,
}: {
  rid: string;
  personaId: string;
  watchForIndex: number;
  onDone: () => void;
}) {
  const [reason, setReason] = useState('');
  const createMut = useMutation({
    mutationFn: () =>
      api.createCalibration({
        rid,
        persona_id: personaId,
        watch_for_index: watchForIndex,
        reason: reason.trim(),
      }),
    onSuccess: () => {
      toast.success(
        'Overrule filed — an admin will review it on the Reviews page. Once approved, the judge stops failing this in future runs.',
      );
      setReason('');
      onDone();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="mt-2 rounded-md border border-status-warning-border/50 bg-status-warning/40 p-3">
      <div className="text-xs font-medium text-status-warning-text">
        Why is the judge wrong here?
      </div>
      <p className="mt-0.5 text-[11px] text-status-warning-text/80">
        Goes to admin approval with the full transcript. Once approved, this
        correction applies to ALL personas in this cell from the next run —
        it never changes verdicts already graded.
      </p>
      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        rows={2}
        placeholder="e.g. 'Saavi' is the approved campaign alias for the agent — name mismatch with ground truth is intentional, not a violation."
        className="mt-2 w-full rounded-md border border-input bg-card p-2 text-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />
      <div className="mt-2 flex justify-end gap-2">
        <button
          onClick={onDone}
          className="rounded-md px-2.5 py-1 text-xs text-muted-foreground hover:text-foreground"
        >
          Cancel
        </button>
        <button
          onClick={() => createMut.mutate()}
          disabled={createMut.isPending || reason.trim().length < 5}
          className="inline-flex items-center gap-1.5 rounded-md bg-status-warning px-2.5 py-1 text-xs font-medium text-status-warning-text hover:bg-status-warning disabled:opacity-50"
        >
          {createMut.isPending ? <Spinner className="size-3" /> : <Scale className="size-3" />}
          File for admin approval
        </button>
      </div>
    </div>
  );
}

function CommentForm({
  rid,
  personaId,
  watchForIndex,
  evidenceQuote,
  onDone,
}: {
  rid: string;
  personaId: string;
  watchForIndex: number;
  evidenceQuote: string;
  onDone: () => void;
}) {
  const [body, setBody] = useState('');
  const [criterion, setCriterion] = useState('');
  const [similar, setSimilar] = useState<SimilarFinding[] | null>(null);
  const [pickedTargets, setPickedTargets] = useState<Set<string>>(new Set());
  const [savedCommentId, setSavedCommentId] = useState<string | null>(null);
  const qc = useQueryClient();

  const addMut = useMutation({
    mutationFn: () =>
      api.addComment(rid, {
        persona_id: personaId,
        watch_for_index: watchForIndex,
        body: body.trim(),
        evidence_quote: evidenceQuote,
        criterion_ids: criterion.trim() ? criterion.split(',').map((s) => s.trim()) : [],
      }),
    onSuccess: (c) => {
      qc.invalidateQueries({ queryKey: ['comments', rid] });
      setSavedCommentId(c.comment_id);
      toast.success('Comment saved');
    },
    onError: (e) => toast.error(String(e)),
  });

  const similarMut = useMutation({
    mutationFn: () =>
      savedCommentId ? api.findSimilar(rid, savedCommentId) : Promise.reject('save first'),
    onSuccess: (r) => setSimilar(r.findings),
    onError: (e) => toast.error(String(e)),
  });

  const applyMut = useMutation({
    mutationFn: () => {
      if (!savedCommentId || !similar) return Promise.reject('nothing to apply');
      const targets = similar
        .filter((f) => pickedTargets.has(`${f.rid}|${f.persona_id}|${f.watch_for_index}`))
        .map((f) => ({ rid: f.rid, persona_id: f.persona_id, watch_for_index: f.watch_for_index }));
      return api.applySimilar(rid, savedCommentId, targets);
    },
    onSuccess: (clones) => {
      toast.success(`Cloned to ${clones.length} target(s)`);
      onDone();
    },
    onError: (e) => toast.error(String(e)),
  });

  return (
    <div className="mt-3 space-y-3 rounded-md border border-border bg-muted/30 p-3">
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={3}
        placeholder="What's wrong with this finding? What should the agent have done?"
        className="w-full rounded-md border border-input bg-card p-2 text-sm shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />
      <div className="flex flex-wrap items-center gap-2">
        <input
          value={criterion}
          onChange={(e) => setCriterion(e.target.value)}
          placeholder="criterion_ids (comma-separated, optional)"
          className="h-8 flex-1 min-w-[200px] rounded-md border border-input bg-card px-2 text-xs shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
        <button
          onClick={() => addMut.mutate()}
          disabled={addMut.isPending || !body.trim() || savedCommentId !== null}
          className="inline-flex h-8 items-center gap-1.5 rounded-md bg-primary px-3 text-xs font-medium text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 disabled:opacity-50"
        >
          {addMut.isPending && <Spinner className="size-3 text-primary-foreground" />}
          {savedCommentId ? 'Saved' : 'Save'}
        </button>
        {savedCommentId && (
          <button
            onClick={() => similarMut.mutate()}
            disabled={similarMut.isPending}
            className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border bg-card px-3 text-xs font-medium hover:bg-accent disabled:opacity-50"
          >
            {similarMut.isPending && <Spinner className="size-3" />}
            <Sparkles className="size-3.5" />
            Resolve similar
          </button>
        )}
        <button
          onClick={onDone}
          className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border bg-card px-3 text-xs font-medium text-muted-foreground hover:bg-accent"
        >
          Close
        </button>
      </div>
      {similar && (
        <div className="space-y-2">
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Similar findings ({similar.length}) — pick to clone the comment
          </div>
          {similar.length === 0 ? (
            <div className="text-xs text-muted-foreground">No matches at Jaccard ≥ 0.15.</div>
          ) : (
            <div className="space-y-1.5">
              {similar.map((f) => {
                const key = `${f.rid}|${f.persona_id}|${f.watch_for_index}`;
                const picked = pickedTargets.has(key);
                return (
                  <label
                    key={key}
                    className={cn(
                      'flex items-start gap-2 rounded-md border bg-card p-2 text-xs',
                      picked ? 'border-primary/40 bg-accent/40' : 'border-border',
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={picked}
                      onChange={() =>
                        setPickedTargets((s) => {
                          const next = new Set(s);
                          if (next.has(key)) next.delete(key);
                          else next.add(key);
                          return next;
                        })
                      }
                      className="mt-0.5"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="font-mono text-[11px] text-muted-foreground">
                        {f.rid} · {f.persona_id} · #{f.watch_for_index} · jaccard {f.jaccard_score} · {f.scribe_version}
                      </div>
                      <div className="mt-0.5 text-sm">{f.watch_for_text}</div>
                      {f.evidence_quote && (
                        <div className="mt-0.5 italic text-muted-foreground">"{f.evidence_quote}"</div>
                      )}
                    </div>
                  </label>
                );
              })}
              <button
                onClick={() => applyMut.mutate()}
                disabled={applyMut.isPending || pickedTargets.size === 0}
                className="inline-flex h-8 items-center gap-1.5 rounded-md bg-primary px-3 text-xs font-medium text-primary-foreground shadow-sm hover:bg-primary/90 disabled:opacity-50"
              >
                {applyMut.isPending ? <Spinner className="size-3 text-primary-foreground" /> : <CheckCircle2 className="size-3.5" />}
                Apply to {pickedTargets.size} selected
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
